import component from '@123/druid/dist/Framework/Decorators/Component';
import {inject, injectable} from '@123/druid/dist/Framework/Decorators/Inject';
import CookieTrackingService from '@Module/Tracking/Service/CookieTrackingService';
import type {ParsedHTML} from '@Module/Tracking/Type/ParsedHTML';
import Measure from '@Service/LegacyBridge/Measure/Measure';
import appendScript from '@Utility/Dom/appendScript';

@component
@injectable()
export default class CookieTracking {
    constructor(@inject(CookieTrackingService) private service: CookieTrackingService) {}

    public async updateCookiePreference(cookiePreference: boolean): Promise<void> {
        const response = await this.service.updateCookiePreferences({
            pageType: window.pageType,
            cookiePreference,
        });

        if (response.isConsentRequired && cookiePreference === false) {
            return;
        }
        const parsedElements = this.parseHTMLByTag(response.html);

        this.insertTrackingScripts(parsedElements);
        await this.insertTemplateScripts(parsedElements, cookiePreference);
    }

    private insertTrackingScripts(parsedElements: ParsedHTML): void {
        parsedElements.script.forEach((scriptElement) => {
            appendScript(document.head, scriptElement);
        });
    }

    private async insertTemplateScripts(elements: ParsedHTML, cookiePreference: boolean): Promise<void> {
        if (cookiePreference && Measure.isRegistered() === false) {
            for (const element of elements.script) {
                appendScript(document.head, element);
            }

            for (const element of elements.meta) {
                document.head.append(element);
            }

            Measure.register();
            await Measure.cookiePermission(cookiePreference, false);
        } else {
            await Measure.cookiePermission(cookiePreference, true);
        }
    }

    private parseHTMLByTag(htmlString: string): ParsedHTML {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;

        const result: ParsedHTML = {script: [], other: [], meta: []};

        tempDiv.childNodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;

            const tagName = node.tagName.toLowerCase();
            if (tagName === 'script') {
                result.script.push(<HTMLScriptElement>node);
            } else if (tagName === 'meta') {
                result.meta.push(<HTMLMetaElement>node);
            } else {
                result.other.push(node);
            }
        });

        return result;
    }
}
